import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { NavigationAction } from '@helpers/Models';
import { useCallback, useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext, Navigator, To } from 'react-router-dom';

/**
 * A React hook that intercepts navigation attempts (e.g., `push` or `replace`)
 * when enabled, and allows you to decide whether to proceed.
 *
 * This is useful for cases like unsaved form changes, where you want to
 * confirm with the user before leaving the current page.
 *
 * @param {boolean} enabled - Whether the navigation blocker is active. If `false`,
 *   navigation behaves normally.
 * @param {(action: NavigationAction) => void} onBlock - A callback invoked when a navigation
 *   attempt is blocked. Receives a `NavigationAction` object with a `retry()` method
 *   that can be called to continue the blocked navigation.
 *
 */
export function useNavigationBlocker(
  enabled: boolean,
  onBlock: (action: NavigationAction) => void,
): void {
  const { navigator } = useContext(UNSAFE_NavigationContext) as {
    navigator: Navigator;
  };

  useEffect(() => {
    if (!enabled) return;

    const push = navigator.push.bind(navigator);
    const replace = navigator.replace.bind(navigator);

    navigator.push = (to: To, state?: string) => {
      onBlock({ retry: () => push(to, state) });
    };

    navigator.replace = (to: To, state?: string) => {
      onBlock({ retry: () => replace(to, state) });
    };

    return () => {
      navigator.push = push;
      navigator.replace = replace;
    };
  }, [enabled, onBlock, navigator]);
}

/**
 * A React hook that blocks navigation when there are unsaved changes
 * and prompts the user with a confirmation toast.
 *
 * If the user chooses to save, the optional `onSave` callback is executed.
 * If the user chooses to continue without saving, the blocked navigation
 * is retried automatically.
 *
 * @param {boolean} hasUnsavedChanges - Whether unsaved changes exist. When `true`,
 *   navigation attempts will be blocked and the user will be prompted.
 * @param {() => void} [onSave] - Optional callback invoked if the user chooses
 *   to save changes before continuing.
 * @param {string} [cancelButtonName] - Optional text for the cancel button.
 *   Defaults to 'Cancel' if not provided.
 *
 */
export function useUnsavedChangesPrompt(
  hasUnsavedChanges: boolean,
  onSave?: () => void,
  cancelButtonName?: string,
): void {
  const { showConfirmationToast } = UseConfirmationToast();

  const handleBlock = useCallback(
    (action: NavigationAction) => {
      showConfirmationToast(
        'You have unsaved changes. Please save before navigating to another tab.',
        'Unsaved Changes',
        'info',
        () => {
          if (onSave) onSave(); // Action button - save callback
        },
        'Stay on This Tab',
        () => {
          action.retry(); // Cancel button - allow navigation
        },
        cancelButtonName,
      );
    },
    [onSave, cancelButtonName],
  );

  useNavigationBlocker(hasUnsavedChanges, handleBlock);
}
